// Who is signed in, and what they may do. The server is the authority; this only shapes the interface.
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, post } from './api';
import type { Me } from './types';

interface AuthApi {
  me: Me | null;
  loading: boolean;
  can: (capability: string) => boolean;
  refresh: () => Promise<unknown>;
  signOut: () => Promise<void>;
  needs2fa: boolean;
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: () => get<Me>('/api/auth/me'),
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
    staleTime: 60_000,
  });

  useEffect(() => {
    const onAuth = (e: Event) => {
      const err = (e as CustomEvent<ApiError>).detail;
      if (err.status === 401) qc.setQueryData(['me'], null);
      else qc.invalidateQueries({ queryKey: ['me'] });
    };
    window.addEventListener('bmp:auth', onAuth);
    return () => window.removeEventListener('bmp:auth', onAuth);
  }, [qc]);

  const me = q.data ?? null;
  const value: AuthApi = {
    me,
    loading: q.isLoading,
    can: (c) => Boolean(me?.capabilities.includes(c)),
    refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
    signOut: async () => { await post('/api/auth/logout').catch(() => {}); qc.clear(); qc.setQueryData(['me'], null); },
    needs2fa: Boolean(me && me.is_staff && !me.totp_enabled),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
